import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { RecipientSource, UpdateWorkOrderRequest, WorkOrderDto } from '@signalvote/shared';
import {
  MessagingProvisioningStatus,
  VoterDataOrderingBlock,
  WorkOrderStatus,
  WorkOrderType,
  WORK_ORDER_MAX_MESSAGE_CHARS,
  assembleTextSendMessage,
  isVoterDataImporting,
} from '@signalvote/shared';
import { ApiError } from '@signalvote/api-client';
import {
  useAttachResultsReport,
  useAttachWorkOrderImage,
  useDownloadResultsReport,
  useCommitteeFacts,
  useDownloadVoterDataFile,
  useFulfilVoterDataOrder,
  useImportVoterDataFile,
  useMessagingProfile,
  useUpdateWorkOrder,
  useUploadWorkOrderRecipients,
  useWorkOrder,
  useWorkOrderCheckoutPreview,
  useVoterDataCatalog,
} from '@/hooks/queries';
import { useAuth } from '@/contexts/AuthContext';
import { useCampaignStore } from '@/stores';
import { useImportProgressStore } from '@/stores/importProgressStore';
import { getCampaignNowTimeString, getCampaignTodayString } from '@/lib/calendarUtils';
import { WorkOrderBuildView, type WorkOrderBuildField } from './WorkOrderBuildView';
import { WorkOrderDetailView } from './WorkOrderDetailView';
import { TextSendResultsReport } from './TextSendResultsReport';
import { progressOfWorkOrder } from './SendChecklist';
import { getWorkOrderScheduleError } from './workOrderSchedule';
import { VoterDataConfirm, useVoterDataCheckout } from './VoterDataConfirm';
import { disclaimerFromPaidForBy, imageUploadErrorMessage } from './TextComposer';
import { WorkOrderPaymentPanel, checkoutMinimumNote, formatCents, useWorkOrderPayment } from './WorkOrderPayment';
import { useBuyList } from './useBuyList';
import { orderingUnavailable } from './voterDataOrder';
import { PendingButton } from '@/components/ui';
import {
  RecipientSourcePicker,
  recipientUploadErrorMessage,
  recipientsFor,
  savedRecipientChoice,
  type RecipientChoice,
} from './RecipientSourcePicker';

interface WorkOrderBuildScreenProps {
  workOrderId: string;
}

/**
 * Data-connected container for the draft build/detail view: fetches the Work Order (with
 * its live recipient count) through {@link useWorkOrder}, wires the image-attach mutation,
 * and owns the self-serve "Submit & Pay" flow — it raises the draft's invoice (threading the
 * current `concurrencyVersion`) and pays it by card in the page, through the same payment the
 * Express review screen uses. The raised order is Invoiced, so the card form shows on its detail.
 * A Voter data Draft pays the same way, after its confirm step has shown the exact price.
 *
 * It also reads the campaign's messaging-profile provisioning status (through the Messaging
 * endpoint — never a Work Orders one, ADR-065 area boundary) and, when the campaign is not
 * yet `Provisioned`, tells the view to note that the paid send waits for registration (ADR-095).
 *
 * The message / send date / send time are **controlled here** and persisted to the draft
 * (PATCH via {@link useUpdateWorkOrder}) on field blur, so the typed content survives a reload
 * and is exactly what "Submit & Pay" invoices — the draft's server state, not local component
 * state. Edits are seeded from the fetched draft (a null local value falls through to server
 * truth); once the operator touches a field the local value wins. Saves are gated by the same
 * guards the server enforces (message ≤ 2,000 chars, send date/time not campaign-local-past), and
 * a server 400 that still slips through is surfaced under the fields.
 */
export function WorkOrderBuildScreen({ workOrderId }: WorkOrderBuildScreenProps) {
  const { data: workOrder, isLoading, isError } = useWorkOrder(workOrderId);
  // A placed Text send shows where it stands, from the committee read and the list it buys.
  const placedTextSend = workOrder?.type === WorkOrderType.TextSend && workOrder.status !== WorkOrderStatus.Draft;
  const { data: committee } = useCommitteeFacts({ enabled: placedTextSend });
  // The list a send to a bought list pays for: its price is confirmed at Pay, and a placed send shows its progress.
  const { data: pairedOrder, isLoading: pairedLoading } = useWorkOrder(
    workOrder?.type === WorkOrderType.TextSend ? (workOrder.details.pairedVoterDataWorkOrderId ?? undefined) : undefined,
  );
  const {
    mutate: attachImage,
    isPending: isUploadingImage,
    error: imageError,
  } = useAttachWorkOrderImage();
  const preview = useWorkOrderCheckoutPreview(workOrderId, workOrder?.type === WorkOrderType.TextSend).data;
  const { mutate: update, error: updateError } = useUpdateWorkOrder();
  const { mutate: uploadRecipients, isPending: isUploadingRecipients, error: uploadError } = useUploadWorkOrderRecipients();
  const paidForBy = useCampaignStore((s) => s.selectedCampaign?.paidForBy);
  const { mutate: fulfil, isPending: isFulfilling, error: fulfilMutationError } = useFulfilVoterDataOrder();
  const { mutate: download, isPending: isDownloading, error: downloadMutationError } = useDownloadVoterDataFile();
  const {
    mutate: downloadResults,
    isPending: isDownloadingResults,
    error: downloadResultsError,
  } = useDownloadResultsReport();
  const { mutate: attachResults, isPending: isAttachingResults, error: attachResultsError } = useAttachResultsReport();
  const {
    mutate: importFile,
    isPending: isImporting,
    isSuccess: importStarted,
    error: importMutationError,
  } = useImportVoterDataFile();
  const bumpImportProgress = useImportProgressStore((s) => s.bumpSse);
  // Delivery starts the import, so the banner may not be following it yet.
  const deliveryImportRunning = isVoterDataImporting(workOrder?.voterData?.importStatus);
  useEffect(() => {
    if (deliveryImportRunning) bumpImportProgress();
  }, [deliveryImportRunning, bumpImportProgress]);
  // Importing is behind the subscription paywall; a 402 marks the campaign lapsed here too.
  const subscriptionStatus = useCampaignStore((s) => s.selectedCampaign?.subscriptionStatus);
  const importLocked = subscriptionStatus === 'Unpaid' || subscriptionStatus === 'Canceled';
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPlatformAdmin = user?.isPlatformAdmin ?? false;

  // A first send is paid before registration and waits for it after (ADR-095), so this is only a
  // note. isFirstSend is already false for a bypassed campaign or a skipped order; a campaign with no
  // profile yet (404 → null) waits like any other that isn't Provisioned. An unanswered read says nothing.
  const profileRead = useMessagingProfile();
  const waitsForRegistration =
    !!workOrder?.isFirstSend &&
    profileRead.status === 'ready' &&
    profileRead.data?.provisioning.status !== MessagingProvisioningStatus.Provisioned;

  // A Voter data Draft is confirmed at its price, and the checkout re-quotes it afresh: a changed
  // price comes back to the confirm step instead of being billed.
  const checkout = useVoterDataCheckout();

  // Local edits to the controlled draft fields. A null value means "untouched" — the derived
  // value below falls through to the server's persisted value, so a background refetch never
  // clobbers what the operator is typing, and a freshly-loaded draft shows its saved content.
  const [draftMessage, setDraftMessage] = useState<string | null>(null);
  const [draftDisclaimer, setDraftDisclaimer] = useState<string | null>(null);
  const [recipientChoice, setRecipientChoice] = useState<RecipientChoice | null>(null);
  const [draftSendDate, setDraftSendDate] = useState<string | null>(null);
  const [draftSendTime, setDraftSendTime] = useState<string | null>(null);

  // An Invoiced Voter data order is paid only while the campaign can buy (the checkout that hands back
  // its payment refuses otherwise), so its payment waits for the catalog's answer. Only the switch
  // counts: an invoice already raised is paid at its own price, whatever the site's price config says.
  const voterDataCatalog = useVoterDataCatalog(undefined, { enabled: workOrder?.type === WorkOrderType.VoterData });
  const ordering = voterDataCatalog.data?.ordering;
  const buyingSwitchedOff = ordering?.reason === VoterDataOrderingBlock.WorkOrdersDisabled;
  const invoicePayable = workOrder?.type !== WorkOrderType.VoterData || (!voterDataCatalog.isLoading && !buyingSwitchedOff);

  // "Submit & Pay" raises the order's invoice and pays it by card in the page, the same way the
  // Express review screen does; an Invoiced order picks that payment back up on its detail.
  const payment = useWorkOrderPayment({
    resume: invoicePayable,
    workOrder,
    totalCents: preview?.totalCents ?? null,
    confirmedVoterDataPriceCents:
      workOrder?.type === WorkOrderType.VoterData
        ? (workOrder.voterData?.priceCents ?? null)
        : (pairedOrder?.voterData?.priceCents ?? null),
  });

  if (isLoading) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Loading work order…
      </p>
    );
  }

  if (isError || !workOrder) {
    return (
      <p role="alert" className="text-sm text-destructive">
        Couldn’t load this work order.
      </p>
    );
  }

  // Only a Draft is editable; every later state is frozen, so it opens the read-only detail
  // instead of the editable build view (no inputs, no Submit & Pay — just its contents + Pay now).
  if (workOrder.status !== WorkOrderStatus.Draft) {
    // Until it reads Paid; then its status badge says so.
    const paying = (workOrder.status === WorkOrderStatus.Invoiced && invoicePayable) || payment.stage === 'received';
    // A platform admin delivers a Paid Voter data order's file from here; the view offers the form
    // only while it is Paid.
    const canFulfil = isPlatformAdmin && workOrder.type === WorkOrderType.VoterData;
    return (
      <WorkOrderDetailView
        workOrder={workOrder}
        progress={
          workOrder.type === WorkOrderType.TextSend
            ? progressOfWorkOrder(workOrder, committee ?? null, pairedOrder ?? null)
            : undefined
        }
        onFulfil={
          canFulfil
            ? (input) =>
                fulfil({
                  id: workOrder.id,
                  body: { ...input, expectedConcurrencyVersion: workOrder.concurrencyVersion },
                })
            : undefined
        }
        fulfilling={isFulfilling}
        fulfilError={fulfilErrorMessage(fulfilMutationError)}
        onDownload={
          workOrder.type === WorkOrderType.VoterData
            ? // Each download signs a new short-lived link; the browser follows it straight away.
              () => download(workOrder.id, { onSuccess: (link) => { window.location.href = link.url; } })
            : undefined
        }
        downloading={isDownloading}
        downloadError={downloadErrorMessage(downloadMutationError)}
        onImport={
          workOrder.type === WorkOrderType.VoterData
            ? // The app shell's import banner follows the job once its progress stream reconnects.
              () => importFile(workOrder.id, { onSuccess: () => bumpImportProgress() })
            : undefined
        }
        importing={isImporting}
        importStarted={importStarted}
        importLocked={importLocked}
        importError={importErrorMessage(importMutationError)}
        payment={paying ? <WorkOrderPaymentPanel payment={payment} withButton /> : undefined}
        results={
          <TextSendResultsReport
            workOrder={workOrder}
            // Each download signs a new short-lived link; the browser follows it straight away.
            onDownload={() => downloadResults(workOrder.id, { onSuccess: (link) => { window.location.href = link.url; } })}
            downloading={isDownloadingResults}
            downloadError={downloadResultsError ? 'Couldn’t start the download. Please try again.' : null}
            onAttach={
              isPlatformAdmin
                ? (file) => attachResults({ id: workOrder.id, file, expectedConcurrencyVersion: workOrder.concurrencyVersion })
                : undefined
            }
            attaching={isAttachingResults}
            attachError={fulfilErrorMessage(attachResultsError, 'Couldn’t attach the report. Please try again.')}
          />
        }
        payUnavailable={buyingSwitchedOff && ordering ? orderingUnavailable(ordering) : undefined}
      />
    );
  }

  // A Voter data order has nothing to edit here — its list is built in the builder and its quote
  // is the server's. A Draft (placed, but never paid) can still be paid, at a fresh quote, while
  // the campaign can still buy.
  if (workOrder.type === WorkOrderType.VoterData) {
    return <VoterDataDraft workOrder={workOrder} checkout={checkout} />;
  }

  // Derived controlled values: a touched local edit wins; otherwise the draft's server truth.
  const message = draftMessage ?? workOrder.details.messageText ?? '';
  // A Draft without a disclaimer starts from the campaign's Paid for by; it saves with the next edit.
  const paidForDisclaimer =
    draftDisclaimer ?? workOrder.details.paidForDisclaimer ?? disclaimerFromPaidForBy(paidForBy);
  const sendDate = draftSendDate ?? workOrder.sendDate ?? '';
  const sendTime = draftSendTime ?? workOrder.sendTime ?? '';

  // The two client-side guards mirroring the server's create/PATCH validation (400 otherwise):
  // message length and a campaign-local-past schedule. Both gate the save AND Submit & Pay.
  const overLimit = assembleTextSendMessage(message, paidForDisclaimer).length > WORK_ORDER_MAX_MESSAGE_CHARS;
  const scheduleError = getWorkOrderScheduleError(
    sendDate,
    sendTime,
    getCampaignTodayString(),
    getCampaignNowTimeString(),
  );

  // Only a Draft is editable — persist is a no-op on any later state (the server would 409/400).
  const isDraft = workOrder.status === WorkOrderStatus.Draft;

  const handleFieldChange = (field: WorkOrderBuildField, value: string) => {
    if (field === 'message') setDraftMessage(value);
    else if (field === 'paidForDisclaimer') setDraftDisclaimer(value);
    else if (field === 'sendDate') setDraftSendDate(value);
    else setDraftSendTime(value);
  };

  // On-blur save (ADR-068 build seam): PATCH the draft with the current field values, but only
  // when this is a Draft, something actually changed, and the client-side guards pass — so a
  // rejected message/schedule never round-trips. An empty date/time is sent as null (the PATCH
  // reads null as "leave unchanged"). Persist does not bump the concurrency token, so a later
  // Submit & Pay still invoices with the version it read.
  const dirty =
    message !== (workOrder.details.messageText ?? '') ||
    paidForDisclaimer !== (workOrder.details.paidForDisclaimer ?? '') ||
    sendDate !== (workOrder.sendDate ?? '') ||
    sendTime !== (workOrder.sendTime ?? '');
  const fields: UpdateWorkOrderRequest = {
    messageText: message,
    paidForDisclaimer,
    sendDate: sendDate || null,
    sendTime: sendTime || null,
    amount: null,
  };

  const handleFieldBlur = () => {
    if (!isDraft || overLimit || scheduleError || !dirty) return;
    update({ id: workOrder.id, body: fields });
  };

  // "Save draft": persist the current fields (if the client-side guards pass and something
  // actually changed) and return to the jobs list — no invoice, no payment. The fields already
  // auto-save on blur; this is the explicit "I'm done, someone can pay it later" action, so a
  // campaign or operator can set a draft up and leave the payment for whoever pays.
  const handleSaveDraft = () => {
    if (overLimit || scheduleError) return;
    if (!dirty) {
      navigate({ to: '/app/work-orders' });
      return;
    }
    update({ id: workOrder.id, body: fields }, { onSuccess: () => navigate({ to: '/app/work-orders' }) });
  };

  // Checkout bills what the server holds, so unsaved edits (such as a prefilled disclaimer) save first.
  const handleSubmitAndPay = () => {
    if (!dirty) {
      payment.pay(workOrder);
      return;
    }
    update({ id: workOrder.id, body: fields }, { onSuccess: (saved) => payment.pay(saved) });
  };

  const pickedRecipients = recipientChoice ?? savedRecipientChoice(workOrder) ?? 'cellPhones';
  const setSource = (recipientSource: RecipientSource) =>
    update({
      id: workOrder.id,
      body: { messageText: null, sendDate: null, sendTime: null, amount: null, recipientSource },
    });

  // Surface the server's own 400 text (e.g. "The message ... may not exceed 2000 characters.") when
  // a persist is rejected — the client guards are the first line, the server the backstop.
  const saveError = updateError ? updateError.message : null;

  return (
    <WorkOrderBuildView
      workOrder={workOrder}
      message={message}
      paidForDisclaimer={paidForDisclaimer}
      sendDate={sendDate}
      sendTime={sendTime}
      onFieldChange={handleFieldChange}
      onFieldBlur={handleFieldBlur}
      scheduleError={scheduleError}
      saveError={saveError}
      onAttachImage={(file) => attachImage({ id: workOrder.id, file, expectedConcurrencyVersion: workOrder.concurrencyVersion })}
      isUploadingImage={isUploadingImage}
      imageUploadError={imageUploadErrorMessage(imageError)}
      onSaveDraft={handleSaveDraft}
      onSubmitAndPay={handleSubmitAndPay}
      priceCents={preview?.totalCents ?? null}
      isSubmitting={payment.stage === 'starting'}
      submitError={payment.error}
      waitsForRegistration={waitsForRegistration}
      minimumNote={checkoutMinimumNote(preview)}
      // A picked source with no list saved yet would bill the one saved before it.
      recipientsSettled={recipientsFor(workOrder, pickedRecipients) !== null}
      recipients={
        <RecipientSourcePicker
          workOrder={workOrder}
          // The build screen has always texted the campaign's cell phones unless told otherwise.
          choice={pickedRecipients}
          onChoiceChange={setRecipientChoice}
          onSourceChange={setSource}
          onUpload={(file) =>
            uploadRecipients({ id: workOrder.id, file, expectedConcurrencyVersion: workOrder.concurrencyVersion })
          }
          uploading={isUploadingRecipients}
          uploadError={recipientUploadErrorMessage(uploadError)}
          // The builder restores the paired list once, when it mounts, so it waits for the quote.
          buy={pairedLoading ? null : <BuyListSlot workOrder={workOrder} pairedOrder={pairedOrder ?? null} />}
        />
      }
    />
  );
}

/** Buy a list on the build screen: the list sentence, then Use this list until the send is paired with its quote. */
function BuyListSlot({ workOrder, pairedOrder }: { workOrder: WorkOrderDto; pairedOrder: WorkOrderDto | null }) {
  const buy = useBuyList(workOrder, pairedOrder);
  return (
    <div className="space-y-3">
      {buy.sentence}
      {buy.isPaired && buy.quote ? (
        <p data-testid="list-quote" className="text-sm text-muted-foreground">
          {buy.quote.records.toLocaleString('en-US')} voters on this list, {formatCents(buy.quote.priceCents)} for the list.
        </p>
      ) : (
        <>
          <PendingButton type="button" variant="outline" pending={buy.pairing} disabled={!buy.canPair} onClick={buy.pair}>
            Use this list
          </PendingButton>
          {buy.unanswered && <p className="text-sm text-muted-foreground">{buy.unanswered}</p>}
        </>
      )}
    </div>
  );
}

/**
 * The server's "file expired" (410) and "not delivered yet" (409) read as-is — it owns that copy;
 * anything else is a generic retry.
 */
function downloadErrorMessage(error: Error | null): string | null {
  if (!error) return null;
  if (error instanceof ApiError && (error.status === 409 || error.status === 410)) return error.message;
  return 'Couldn’t start the download. Please try again.';
}

/**
 * The server's refusals read as-is — a file that can't be imported (400), not delivered yet (409) or
 * expired (410); anything else is a generic retry.
 */
function importErrorMessage(error: Error | null): string | null {
  if (!error) return null;
  if (error instanceof ApiError && [400, 409, 410].includes(error.status)) return error.message;
  return 'Couldn’t start the import. Please try again.';
}

/**
 * The server's refusal reads as-is (a file missing its columns, a count needing the customer
 * contacted, someone else's change); anything else is a generic retry.
 */
function fulfilErrorMessage(error: Error | null, fallback = 'Couldn’t deliver the file. Please try again.'): string | null {
  if (!error) return null;
  if (error instanceof ApiError && (error.status === 400 || error.status === 409)) return error.message;
  return fallback;
}

/**
 * A Voter data Draft's page. It offers Pay only once the catalog's ordering check, the one checkout
 * refuses on, says the campaign can buy; otherwise it says why, so a campaign that can no longer buy
 * never reaches a payment screen that refuses it.
 */
function VoterDataDraft({ workOrder, checkout }: { workOrder: WorkOrderDto; checkout: ReturnType<typeof useVoterDataCheckout> }) {
  const ordering = useVoterDataCatalog().data?.ordering;
  return (
    <>
      <WorkOrderDetailView
        workOrder={workOrder}
        onPay={ordering?.available ? () => checkout.confirm(workOrder) : undefined}
        payUnavailable={ordering && !ordering.available ? orderingUnavailable(ordering) : undefined}
      />
      <VoterDataConfirm checkout={checkout.checkout} estimate={null} payment={checkout.payment} onClose={checkout.close} />
    </>
  );
}
